/**
 * Telegram Deployment Settings Schema
 *
 * Zod schema for Deployment.deploymentSettings when type = TELEGRAM.
 * Single source of truth for Telegram deployment configuration.
 */

import { z } from "zod";

import type { DeepPartial } from "../../../shared/core/common.types";
import { AdvancedSettingsSchema, DEFAULT_ADVANCED_SETTINGS } from "./Advanced.schema";
import { AssistantSettingsSchema, DEFAULT_ASSISTANT_SETTINGS } from "./Assistant.schema";
import { BaseSettingsSchema } from "./Base.schema";

// ============================================================
// MAIN SCHEMA (Complete Telegram Deployment Settings)
// ============================================================

/**
 * Complete Telegram Deployment Settings Schema
 *
 * This is the SINGLE SOURCE OF TRUTH for Deployment.deploymentSettings
 * when type = "TELEGRAM". Stored as JSON in Prisma, validated with this schema.
 *
 * NOTE: agentId is NOT in deploymentSettings! It's a Deployment column (FK to Agent).
 * agentId was moved from JSON to column. Pass agentId separately when creating deployments.
 */
export const TelegramDeploymentSettingsSchema = z
  .object({
    // Bot token from BotFather
    botToken: z.string(),

    // Initial message sent when user starts conversation
    initialMessage: z.string().optional(),

    // Shared settings
    assistantSettings: AssistantSettingsSchema,
    advancedSettings: AdvancedSettingsSchema
  })
  .merge(BaseSettingsSchema);

export type TelegramDeploymentSettings = z.infer<typeof TelegramDeploymentSettingsSchema>;

// ============================================================
// DEFAULTS (Single Source of Truth)
// ============================================================

/**
 * Default values for creating a new Telegram deployment.
 * Used to initialize forms and ensure no missing fields.
 *
 * Note: botToken is required but defaults to empty - user must provide it.
 */
export const DEFAULT_TELEGRAM_SETTINGS: TelegramDeploymentSettings = {
  botToken: "", // Required - user must provide
  initialMessage: "",
  assistantSettings: DEFAULT_ASSISTANT_SETTINGS,
  advancedSettings: DEFAULT_ADVANCED_SETTINGS
};

// ============================================================
// FACTORY FUNCTION (Type-Safe Creation)
// ============================================================

/**
 * Create complete TELEGRAM deployment settings with defaults.
 *
 * This is the RECOMMENDED way to create Telegram deployment settings.
 * Ensures all required fields are present with sensible defaults.
 *
 * NOTE: agentId is now Deployment column (FK to Agent).
 * This function returns only the settings JSON - agentId is passed separately
 * when creating/updating a Deployment.
 *
 * @param overrides - Partial settings to override defaults (optional)
 * @returns Complete, type-safe TelegramDeploymentSettings
 *
 * @example
 * // Create with defaults (botToken must be set later)
 * const settings = createTelegramDeploymentSettings();
 *
 * @example
 * // Create with bot token
 * const settings = createTelegramDeploymentSettings({
 *   botToken: "123456:ABC-DEF...",
 *   initialMessage: "Welcome!"
 * });
 */
export function createTelegramDeploymentSettings(
  overrides?: DeepPartial<TelegramDeploymentSettings>
): TelegramDeploymentSettings {
  if (!overrides) return { ...DEFAULT_TELEGRAM_SETTINGS };

  const merge = <T extends Record<string, unknown>>(base: T, override?: DeepPartial<T>): T => {
    if (!override) return base;
    const result = { ...base };
    for (const key in override) {
      if (override[key] !== undefined) {
        result[key] = override[key] as T[Extract<keyof T, string>];
      }
    }
    return result;
  };

  return {
    botToken: overrides.botToken ?? DEFAULT_TELEGRAM_SETTINGS.botToken,
    initialMessage: overrides.initialMessage ?? DEFAULT_TELEGRAM_SETTINGS.initialMessage,
    assistantSettings: merge(
      DEFAULT_TELEGRAM_SETTINGS.assistantSettings,
      overrides.assistantSettings
    ),
    advancedSettings: merge(DEFAULT_TELEGRAM_SETTINGS.advancedSettings, overrides.advancedSettings)
  };
}
